/**
 * The "Voice connected" panel pinned to the bottom-left of the app, exactly
 * where Discord puts it: status and channel on top, the call controls under
 * them, then you. It lives in the sidebar shell — outside the swappable sidebar
 * content — so it survives every navigation, and it renders nothing at all
 * when you're not in a channel.
 *
 * Phones get a thinner strip in the content column instead (VoiceDock): there
 * the sidebar is a drawer, and a hang-up button hidden behind a drawer is no
 * hang-up button.
 */
import {
  Captions,
  Loader2,
  Mic,
  MicOff,
  MonitorOff,
  MonitorUp,
  PhoneOff,
  Radio,
  Volume2,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { canShareScreen, useVoiceRoom } from '../../contexts/VoiceRoomContext';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../tasks/linear/Avatar';
import { AvatarWithPresence } from './PresenceDot';
import { LiveBadge, voiceChannelPath } from './VoiceChannelRow';

const CONTROL_CLASS =
  'inline-flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-md px-2 text-[12px] font-medium transition-colors';

function ControlButton({
  icon,
  label,
  pressed,
  danger,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  pressed?: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      className={[
        CONTROL_CLASS,
        danger
          ? 'bg-red-500/15 text-red-600 hover:bg-red-500/25'
          : pressed
            ? 'bg-foreground text-background hover:bg-foreground/90'
            : 'bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent/80',
      ].join(' ')}
    >
      {icon}
    </button>
  );
}

function ConnectedPanel() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const {
    channelId,
    channelName,
    status,
    transcribing,
    micEnabled,
    screenShareEnabled,
    screenShares,
    leave,
    toggleMic,
    toggleScreenShare,
  } = useVoiceRoom();

  // Amber while the SDK re-establishes the connection: green promises that
  // the others can hear you, and during a reconnect that promise is false.
  const settling = status !== 'connected';
  const tone = status === 'reconnecting' ? 'text-amber-600' : 'text-emerald-600';
  const label =
    status === 'connecting'
      ? t('teamChat.voiceConnecting')
      : status === 'reconnecting'
        ? t('teamChat.voiceReconnecting')
        : t('teamChat.voiceConnected');
  const remoteShares = screenShares.filter((s) => !s.isLocal);

  return (
    <>
      <div className="px-3 pt-2.5">
        <div className="flex items-center gap-2.5">
          {/* The status block is the way back into the room: from any page of
              the app, one click lands on the stage, the people and the
              transcript. The hang-up stays a sibling — it must never be one
              mis-tap away from "open". */}
          <button
            type="button"
            onClick={() => channelId && navigate(voiceChannelPath(channelId))}
            title={t('teamChat.voiceOpenChannel')}
            className="-mx-1.5 -my-1 flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-sidebar-accent"
          >
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                status === 'reconnecting' ? 'bg-amber-500/15' : 'bg-emerald-500/15'
              } ${tone}`}
            >
              {settling ? <Loader2 size={18} className="animate-spin" /> : <Radio size={18} />}
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block truncate text-[13px] font-semibold ${tone}`}>{label}</span>
              <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                <span className="truncate">{channelName}</span>
                {/* Said, not hidden: people should know the call is being
                    written down. */}
                {transcribing && (
                  <span
                    title={t('teamChat.voiceTranscriptHint')}
                    className="shrink-0 text-emerald-600"
                  >
                    <Captions size={12} />
                  </span>
                )}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => void leave()}
            title={t('teamChat.voiceLeave')}
            aria-label={t('teamChat.voiceLeave')}
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-red-500/15 hover:text-red-600"
          >
            <PhoneOff size={18} />
          </button>
        </div>

        <div className="mt-2 flex gap-1.5">
          {/* Absent, not disabled, where screen capture cannot work at all: on
              iOS `getDisplayMedia` does not exist in any browser. A greyed-out
              button would imply a fix. */}
          {canShareScreen() && (
            <ControlButton
              icon={screenShareEnabled ? <MonitorOff size={16} /> : <MonitorUp size={16} />}
              label={screenShareEnabled ? t('teamChat.voiceStopSharing') : t('teamChat.voiceShare')}
              pressed={screenShareEnabled}
              onClick={() => void toggleScreenShare()}
            />
          )}
          <ControlButton
            icon={micEnabled ? <Mic size={16} /> : <MicOff size={16} />}
            label={micEnabled ? t('teamChat.voiceMute') : t('teamChat.voiceUnmute')}
            pressed={!micEnabled}
            onClick={() => void toggleMic()}
          />
          {/* Someone is presenting: one click lands on their screen. This is
              the affordance the sidebar LIVE badge points at, repeated here so
              it is reachable from any page of the app. */}
          {channelId && remoteShares.length > 0 && (
            <button
              type="button"
              onClick={() => navigate(voiceChannelPath(channelId, remoteShares[0]!.publisherId))}
              title={t('teamChat.voiceWatch')}
              className={`${CONTROL_CLASS} bg-red-500/15 text-red-600 hover:bg-red-500/25`}
            >
              <LiveBadge />
              <span className="truncate">{t('teamChat.voiceWatch')}</span>
            </button>
          )}
        </div>
      </div>

      {/* You, the way Discord's user panel shows you: this is where the eye
          goes to check "am I muted?", so the mic state is repeated here. */}
      <div className="mt-2.5 flex items-center gap-2.5 border-t border-sidebar-border px-3 py-2">
        <AvatarWithPresence online>
          <Avatar name={me?.name ?? null} size={30} />
        </AvatarWithPresence>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-sidebar-foreground">{me?.name}</p>
          <p className={`flex items-center gap-1 text-[11px] ${tone}`}>
            <Volume2 size={11} />
            {t('teamChat.voiceInVoice')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void toggleMic()}
          title={micEnabled ? t('teamChat.voiceMute') : t('teamChat.voiceUnmute')}
          aria-pressed={!micEnabled}
          className={`rounded-md p-1.5 transition-colors hover:bg-sidebar-accent ${
            micEnabled ? 'text-muted-foreground' : 'text-red-600'
          }`}
        >
          {micEnabled ? <Mic size={16} /> : <MicOff size={16} />}
        </button>
      </div>
    </>
  );
}

/**
 * A join that failed leaves you nowhere — no panel, no channel — so without
 * this the click would simply do nothing and look like a dead button.
 */
function ErrorNotice() {
  const { t } = useI18n();
  const { error, dismissError } = useVoiceRoom();
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <Volume2 size={14} className="shrink-0 text-red-600" />
      <span className="min-w-0 flex-1 text-[12px] leading-snug text-red-600">{t(error!)}</span>
      <button
        type="button"
        onClick={dismissError}
        title={t('common.close')}
        aria-label={t('common.close')}
        className="rounded p-1 text-red-600 hover:bg-red-500/15"
      >
        <X size={13} />
      </button>
    </div>
  );
}

export function VoiceSidebarPanel() {
  const { channelId, error } = useVoiceRoom();
  if (!channelId && !error) return null;
  return (
    <div className="shrink-0 border-t border-sidebar-border bg-sidebar pb-[env(safe-area-inset-bottom)]">
      {channelId ? <ConnectedPanel /> : <ErrorNotice />}
    </div>
  );
}
